import { useEffect, useState } from "react";
import { Grid, TextField, Container, Typography, Box } from "@mui/material";
import Secao from "../../../util/ui/secao";
import MoedaInput from "../../../util/ui/inputs/moedaInput";
import Abatimentos from "../../../../models/abatimentos";
import moment, { Moment } from "moment";
import { useUsuario } from "../../../../providers/usuarioProvider";
import { IconArrowDownCircle } from "@tabler/icons-react";
import Functions from "../../../../callable/functions";
import Imposto from "../../../../models/imposto";
import { onSnapshot } from "firebase/firestore";
import ImpostoDb from "../../../../db/impostoDb";
import { LoadingButton } from "@mui/lab";

type Props = {
  dataBase: Moment;
};

export default function AbatimentosPontoDePartida({ dataBase }: Props) {
  const { visualizado } = useUsuario();
  const [abatimentos, setAbatimentos] = useState<Abatimentos>({
    irrf: 0,
    prejuizo: 0,
    operacoesComuns: { irrf: 0, prejuizo: 0 },
    operacoesDaytrade: { irrf: 0, prejuizo: 0 },
    operacoesFii: { irrf: 0, prejuizo: 0 },
  });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        new ImpostoDb().doc({
          usuarioId: visualizado,
          impostoId: dataBase.format("YYYY-MM"),
        }),
        (snapshot) => {
          if (snapshot.exists()) {
            const pontoDePartida = snapshot.data()!;
            setAbatimentos(pontoDePartida.abatimentos);
          }
        }
      );
  }, [dataBase, visualizado]);

  const salvar = async () => {
    setSalvando(true);
    return new Functions()
      .pontoDePartida({
        pontoDePartida: new Imposto({
          abatimentos,
          criadoEm: moment(),
          dataBase: dataBase,
          pontoDePartida: true,
        }).toJSON(),
      })
      .finally(() => setSalvando(false));
  };

  const somarAbatimentos = (value: Abatimentos) => {
    value.irrf =
      value.operacoesComuns.irrf +
      value.operacoesDaytrade.irrf +
      value.operacoesFii.irrf;
    value.prejuizo =
      value.operacoesComuns.prejuizo +
      value.operacoesDaytrade.prejuizo +
      value.operacoesFii.prejuizo;

    return value;
  };

  async function setIrrfComuns(valor: number) {
    setAbatimentos((prev) =>
      somarAbatimentos({
        ...prev,
        operacoesComuns: {
          ...prev.operacoesComuns,
          irrf: valor,
        },
      })
    );
  }

  async function setPrejuizoComuns(valor: number) {
    setAbatimentos((prev) =>
      somarAbatimentos({
        ...prev,
        operacoesComuns: {
          ...prev.operacoesComuns,
          prejuizo: valor,
        },
      })
    );
  }

  async function setIrrfDayTrade(valor: number) {
    setAbatimentos((prev) =>
      somarAbatimentos({
        ...prev,
        operacoesDaytrade: {
          ...prev.operacoesDaytrade,
          irrf: valor,
        },
      })
    );
  }

  async function setPrejuizoDayTrade(valor: number) {
    setAbatimentos((prev) =>
      somarAbatimentos({
        ...prev,
        operacoesDaytrade: {
          ...prev.operacoesDaytrade,
          prejuizo: valor,
        },
      })
    );
  }

  async function setIrrfFii(valor: number) {
    setAbatimentos((prev) =>
      somarAbatimentos({
        ...prev,
        operacoesFii: {
          ...prev.operacoesFii,
          irrf: valor,
        },
      })
    );
  }

  async function setPrejuizoFii(valor: number) {
    setAbatimentos((prev) =>
      somarAbatimentos({
        ...prev,
        operacoesFii: {
          ...prev.operacoesFii,
          prejuizo: valor,
        },
      })
    );
  }

  return (
    <Container fixed disableGutters sx={{ py: 1 }}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        paddingBottom={2}
      >
        <Typography variant="h3" color="text.primary">
          Prejuízos acumulados até{" "}
          <Typography
            component="span"
            variant="dataMono"
            sx={{ fontSize: "inherit", fontWeight: 500 }}
          >
            {dataBase.clone().endOf("month").format("DD/MM/YYYY")}
          </Typography>
        </Typography>
        <Box paddingX={1} />
        <LoadingButton
          variant="contained"
          size="small"
          color="success"
          onClick={salvar}
          loadingPosition="start"
          startIcon={<IconArrowDownCircle />}
          loading={salvando}
        >
          Salvar
        </LoadingButton>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Secao titulo="Operações Comuns">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  id="standard-comuns-irrf"
                  label="I.R.R.F."
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={abatimentos.operacoesComuns.irrf}
                  onChange={async (e) => {
                    const floatValue: number = (e.target as any).floatValue;
                    await setIrrfComuns(floatValue);
                  }}
                  InputProps={{
                    inputComponent: MoedaInput,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="standard-comuns-prejuizo"
                  label="Prejuízo"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={abatimentos.operacoesComuns.prejuizo}
                  onChange={async (e) => {
                    const floatValue: number = (e.target as any).floatValue;
                    await setPrejuizoComuns(floatValue);
                  }}
                  InputProps={{
                    inputComponent: MoedaInput,
                  }}
                />
              </Grid>
            </Grid>
          </Secao>
        </Grid>
        <Grid item xs={12} md={3}>
          <Secao titulo="Day Trade">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  id="standard-daytrade-irrf"
                  label="I.R.R.F."
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={abatimentos.operacoesDaytrade.irrf}
                  onChange={async (e) => {
                    const floatValue: number = (e.target as any).floatValue;
                    await setIrrfDayTrade(floatValue);
                  }}
                  InputProps={{
                    inputComponent: MoedaInput,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="standard-daytrade-prejuizo"
                  label="Prejuízo"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={abatimentos.operacoesDaytrade.prejuizo}
                  onChange={async (e) => {
                    const floatValue: number = (e.target as any).floatValue;
                    await setPrejuizoDayTrade(floatValue);
                  }}
                  InputProps={{
                    inputComponent: MoedaInput,
                  }}
                />
              </Grid>
            </Grid>
          </Secao>
        </Grid>
        <Grid item xs={12} md={3}>
          <Secao titulo="Fundos Imobiliários">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  id="standard-fii-irrf"
                  label="I.R.R.F."
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={abatimentos.operacoesFii.irrf}
                  onChange={async (e) => {
                    const floatValue: number = (e.target as any).floatValue;
                    await setIrrfFii(floatValue);
                  }}
                  InputProps={{
                    inputComponent: MoedaInput,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="standard-fii-prejuizo"
                  label="Prejuízo"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={abatimentos.operacoesFii.prejuizo}
                  onChange={async (e) => {
                    const floatValue: number = (e.target as any).floatValue;
                    await setPrejuizoFii(floatValue);
                  }}
                  InputProps={{
                    inputComponent: MoedaInput,
                  }}
                />
              </Grid>
            </Grid>
          </Secao>
        </Grid>
        <Grid item xs={12} md={3}>
          <Secao titulo="Total">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  id="standard-fii-irrf"
                  label="I.R.R.F."
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  size="small"
                  disabled
                  fullWidth
                  value={abatimentos.irrf}
                  sx={{ "& input": { fontWeight: 500 } }}
                  InputProps={{
                    inputComponent: MoedaInput,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="standard-fii-prejuizo"
                  label="Prejuízo"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  size="small"
                  disabled
                  fullWidth
                  value={abatimentos.prejuizo}
                  sx={{ "& input": { fontWeight: 500 } }}
                  InputProps={{
                    inputComponent: MoedaInput,
                  }}
                />
              </Grid>
            </Grid>
          </Secao>
        </Grid>
      </Grid>
    </Container>
  );
}
